import { useRef, useState } from "react";
import { usePortal } from "../store";

function downloadJson(name:string,value:unknown) {
  const blob=new Blob([JSON.stringify(value,null,2)],{type:"application/json"});
  const link=document.createElement("a");
  link.href=URL.createObjectURL(blob);
  link.download=name;
  link.click();
  setTimeout(()=>URL.revokeObjectURL(link.href),0);
}

function stamp() {
  return new Date().toISOString().replace(/[:.]/g,"-");
}

export function WorkspaceRecoveryTools({fullPage=false}:{fullPage?:boolean}) {
  const {workspace,recovery}=usePortal();
  const input=useRef<HTMLInputElement>(null);
  const [busy,setBusy]=useState(false);
  const [showDetails,setShowDetails]=useState(fullPage);
  const previous=recovery.previousWorkspaceKeys();

  async function exportOriginal() {
    setBusy(true);
    try{
      const value=await recovery.exportOriginalWorkspace(),suffix=stamp();
      downloadJson(`workspace-recovery-manifest-${suffix}.json`,value.manifest);
      downloadJson(`workspace-recovery-raw-sources-${suffix}.json`,value.sources);
    }finally{setBusy(false)}
  }

  async function load(file:File) {
    setBusy(true);
    try{await recovery.loadCandidateIntoStaging(await file.text(),file.name)}
    finally{setBusy(false);if(input.current)input.current.value=""}
  }
  async function run(action:()=>Promise<unknown>) {
    setBusy(true);
    try{await action()}catch{/* the recovery controller exposes the error without changing the primary workspace */}
    finally{setBusy(false)}
  }

  const content=<>
    <div className="workspace-recovery-heading">
      <div>
        <p className="eyebrow">Local workspace safety</p>
        <h1>{fullPage?"Workspace recovery required":"Review recovery details"}</h1>
      </div>
      {!fullPage&&<button onClick={()=>setShowDetails(value=>!value)}>{showDetails?"Close details":"Review recovery details"}</button>}
    </div>
    {(fullPage||showDetails)&&<div className="workspace-recovery-content">
      {fullPage&&<p className="recovery-lead">Your saved workspace could not be loaded safely. Nothing has been overwritten.</p>}
      <p>Autosave is {workspace.recoveryInProgress?"paused":"protected by validation and checksummed migration backups"}.</p>
      {workspace.startup.kind==="recovery-required"&&<details open><summary>Technical details</summary><p><strong>{workspace.startup.reason}:</strong> {workspace.startup.error}</p></details>}
      {workspace.message&&<p className="notice" role="status">{workspace.message}</p>}
      {workspace.candidateIdentity&&<p className="notice"><strong>Candidate workspace:</strong> {workspace.candidateIdentity.kind} · {workspace.candidateIdentity.workspaceId}</p>}
      {workspace.error&&<p className="validation-error" role="alert">{workspace.error}</p>}
      <div className="recovery-actions">
        <button className="primary-button" disabled={busy} onClick={exportOriginal}>Export original workspace</button>
        <button disabled={busy} onClick={()=>input.current?.click()}>Load candidate into staging</button>
        <input ref={input} hidden type="file" accept=".json,application/json" onChange={event=>{const file=event.target.files?.[0];if(file)void load(file)}}/>
        <button disabled={busy||!workspace.stagedKey} onClick={()=>void run(recovery.validateCandidate)}>Validate candidate</button>
        <button disabled={busy||!workspace.validatedKey} onClick={()=>void run(recovery.useStagedWorkspace)}>{workspace.candidateIdentity?.kind==="operational"&&workspace.candidateIdentity.workspaceId!==workspace.startup.state.workspaceMetadata?.workspaceId?"Activate recovered operational workspace":workspace.candidateIdentity?.kind==="demonstration"?"Activate demonstration workspace":workspace.candidateIdentity?.kind==="legacy-unclassified"?"Activate legacy workspace":"Use staged workspace"}</button>
        {workspace.candidateIdentity?.kind==="operational"&&workspace.candidateIdentity.workspaceId!==workspace.startup.state.workspaceMetadata?.workspaceId&&<p>The recovered workspace will retain its original identity. The current workspace remains preserved for rollback. No demonstration or fixture conversion will occur.</p>}
        <button disabled={busy||previous.length===0} onClick={()=>void run(()=>recovery.restorePrevious(previous.at(-1)))}>Restore previous workspace</button>
        {workspace.stagedKey&&<button disabled={busy} onClick={recovery.cancelStaging}>Cancel staging</button>}
      </div>
      {workspace.stagedComparison&&<section className="recovery-comparison" aria-label="Staged workspace comparison">
        <h2>Validation comparison</h2>
        <table><thead><tr><th>Records</th><th>Candidate</th><th>After migration</th></tr></thead><tbody>
          <tr><th>Organisations</th><td>{workspace.stagedComparison.before.organisations}</td><td>{workspace.stagedComparison.after.organisations}</td></tr>
          <tr><th>Audits</th><td>{workspace.stagedComparison.before.audits}</td><td>{workspace.stagedComparison.after.audits}</td></tr>
          <tr><th>Reports</th><td>{workspace.stagedComparison.before.reports}</td><td>{workspace.stagedComparison.after.reports}</td></tr>
          <tr><th>Versions</th><td>{workspace.stagedComparison.before.versions}</td><td>{workspace.stagedComparison.after.versions}</td></tr>
        </tbody></table>
        <p>Section 10 identities found: {workspace.stagedComparison.after.sectionTenIdentities.join(", ")||"None"}</p>
        <p>Using the staged workspace creates an exact backup of the current primary value. It does not approve, complete, publish or regenerate any report.</p>
      </section>}
    </div>}
  </>;

  return fullPage?<main className="workspace-recovery-page"><section className="workspace-recovery-card" role="alert">{content}</section></main>:<section className="workspace-recovery-tools">{content}</section>;
}
